import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import { getTranslation } from 'helpers/getTranslation';

import { showToast } from 'helpers/showToast';
import { copyToClipboard } from 'helpers/copyToClipboard';

import { getSchoolCodes } from 'store/codes/actions';
import { userSelector } from 'store/auth/selectors';
import { schoolCodesSelector } from 'store/codes/selectors';

import * as api from 'api/methods';

import { buildSchoolCodeFilterParams } from './ActivationCodes.helpers';

import ActivationCodes from './ActivationCodes';

const ActivationCodesContainer = ({
  activationFrom = '',
  activationTo = '',
  schoolPrefix = '',
  onActivationFromChange = () => {},
  onActivationToChange = () => {},
  onSchoolPrefixChange = () => {},
}: {
  activationFrom?: string;
  activationTo?: string;
  schoolPrefix?: string;
  onActivationFromChange?: (value: string) => void;
  onActivationToChange?: (value: string) => void;
  onSchoolPrefixChange?: (value: string) => void;
}) => {
  const dispatch = useAppDispatch();

  const { id: paramSchoolId } = useParams();

  const codes = useAppSelector(schoolCodesSelector);
  const user = useAppSelector(userSelector);

  const schoolId = paramSchoolId || user.schoolId;

  const [loading, setLoading] = useState(true);
  const [prefixesLoading, setPrefixesLoading] = useState(true);
  const [schoolPrefixOptions, setSchoolPrefixOptions] = useState<string[]>([]);
  const [sortParams, setSortParams] = useState({
    sortBy: 'startDate',
    sortDirection: 'asc',
  });

  const listFilterParams = useMemo(
    () =>
      buildSchoolCodeFilterParams({
        activationFrom,
        activationTo,
        schoolPrefix,
      }),
    [activationFrom, activationTo, schoolPrefix],
  );

  const fetchCodes = useCallback(
    async ({
      page,
      limit,
      sortBy,
      sortDirection,
    }: {
      page: number;
      limit: number;
      sortBy: string;
      sortDirection: string;
    }) => {
      await dispatch(
        getSchoolCodes({
          schoolId,
          params: {
            page,
            limit,
            sortBy,
            sortDirection,
            ...listFilterParams,
          },
        }),
      ).unwrap();
    },
    [dispatch, listFilterParams, schoolId],
  );

  useEffect(() => {
    const loadCodes = async () => {
      setLoading(true);

      try {
        await fetchCodes({
          page: 1,
          limit: 50,
          sortBy: sortParams.sortBy,
          sortDirection: sortParams.sortDirection,
        });
      } catch (error) {
        console.warn(error);
      }

      setLoading(false);
    };

    void loadCodes();
  }, [fetchCodes, sortParams.sortBy, sortParams.sortDirection]);

  useEffect(() => {
    const loadSchoolPrefixes = async () => {
      try {
        const response = (await api.getSchoolCodePrefixes(
          schoolId,
        )) as { data?: string[] };

        setSchoolPrefixOptions(response.data ?? []);
      } catch (error) {
        console.warn(error);
      }

      setPrefixesLoading(false);
    };

    loadSchoolPrefixes();
  }, [schoolId]);

  const handleSortChange = async (nextSortBy: string) => {
    const nextSortDirection =
      sortParams.sortDirection === 'asc' ? 'desc' : 'asc';

    try {
      await fetchCodes({
        page: 1,
        limit: 50,
        sortBy: nextSortBy,
        sortDirection: nextSortDirection,
      });

      setSortParams({
        sortBy: nextSortBy,
        sortDirection: nextSortDirection,
      });
    } catch (error) {
      console.warn(error);
    }
  };

  const handleCopyCodeClick = (code: string) => {
    copyToClipboard(code);

    showToast({
      data: {
        variant: 'success',
        title: getTranslation('common.toast.clipboard_copy.title'),
        text: getTranslation('common.toast.clipboard_copy.text'),
      },
    });
  };

  return (
    <ActivationCodes
      loading={loading}
      codes={codes.data}
      schoolId={schoolId}
      sortParams={sortParams}
      listFilterParams={listFilterParams}
      activationFrom={activationFrom ?? ''}
      activationTo={activationTo ?? ''}
      schoolPrefix={schoolPrefix ?? ''}
      schoolPrefixOptions={schoolPrefixOptions}
      prefixesLoading={prefixesLoading}
      onActivationFromChange={onActivationFromChange}
      onActivationToChange={onActivationToChange}
      onSchoolPrefixChange={onSchoolPrefixChange}
      onSortChange={handleSortChange}
      onCopyCodeClick={handleCopyCodeClick}
    />
  );
};

export default React.memo(ActivationCodesContainer);
